import { useState, useEffect } from 'react';
import { overtimeAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, History, Plus, Minus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function OvertimeDialog({ open, onClose, employee, onSave }) {
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (open && employee) {
      fetchHistory();
      setHours('');
      setReason('');
    }
  }, [open, employee]);

  const fetchHistory = async () => {
    if (!employee) return;
    setHistoryLoading(true);
    try {
      const res = await overtimeAPI.history(employee.id);
      setHistory(res.data);
    } catch {
      // ignore
    }
    setHistoryLoading(false);
  };

  const handleAdjust = async () => {
    const h = parseFloat(hours);
    if (isNaN(h) || h === 0) {
      toast.error('Entrez un nombre valide');
      return;
    }
    if (!reason.trim()) {
      toast.error('Entrez une raison');
      return;
    }
    setLoading(true);
    try {
      await overtimeAPI.adjust(employee.id, { hours: h, reason: reason.trim() });
      toast.success('Heures supplémentaires ajustées');
      onSave();
      fetchHistory();
      setHours('');
      setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="overtime-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" strokeWidth={1.5} />
            Heures supplémentaires
          </DialogTitle>
          <DialogDescription>
            {employee.first_name} {employee.last_name.toUpperCase()} - Solde actuel:{' '}
            <span className={`font-semibold font-data ${employee.overtime_balance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {employee.overtime_balance >= 0 ? '+' : ''}{employee.overtime_balance}h
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Adjustment form */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <Label className="text-sm font-semibold">Ajustement manuel</Label>
            <div className="flex gap-2">
              <Input
                data-testid="overtime-hours-input"
                type="number"
                step="0.25"
                placeholder="ex: 2.5 ou -1.5"
                value={hours}
                onChange={e => setHours(e.target.value)}
                className="font-data flex-1"
              />
            </div>
            <Input
              data-testid="overtime-reason-input"
              placeholder="Raison de l'ajustement..."
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <Button
              data-testid="overtime-adjust-btn"
              onClick={handleAdjust}
              disabled={loading}
              className="w-full"
              size="sm"
            >
              {loading ? 'Enregistrement...' : 'Appliquer l\'ajustement'}
            </Button>
          </div>

          <Separator />

          {/* History */}
          <div>
            <Label className="flex items-center gap-1.5 mb-3 text-sm font-semibold">
              <History className="w-3.5 h-3.5" strokeWidth={1.5} />
              Historique des modifications
            </Label>
            <ScrollArea className="h-[200px]">
              {historyLoading ? (
                <div className="text-sm text-slate-400 text-center py-4">Chargement...</div>
              ) : history.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-4">Aucun historique</div>
              ) : (
                <div className="space-y-2">
                  {history.map((entry) => (
                    <div
                      key={entry.id}
                      data-testid={`overtime-history-${entry.id}`}
                      className="bg-white border border-slate-200 rounded-lg p-3 text-sm animate-fade-in-up"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-1.5">
                          {entry.adjustment >= 0 ? (
                            <Plus className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2} />
                          ) : (
                            <Minus className="w-3.5 h-3.5 text-rose-500" strokeWidth={2} />
                          )}
                          <span className={`font-semibold font-data ${entry.adjustment >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {entry.adjustment >= 0 ? '+' : ''}{entry.adjustment}h
                          </span>
                        </span>
                        <span className="text-xs text-slate-400 font-data">{formatDateTime(entry.created_at)}</span>
                      </div>
                      <p className="text-slate-600">{entry.reason}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Par {entry.modified_by} ({entry.old_balance}h → {entry.new_balance}h)
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
